import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { PageHeader } from "~/components/page-header";
import { DataTable } from "~/components/data-table";
import { MasterDetail } from "~/components/master-detail";
import { SearchInput } from "~/components/search-input";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { DetailField } from "~/components/detail-field";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/components/ui/select";
import { useConfirm } from "~/components/confirm-dialog";
import { api, APIError } from "~/lib/api";
import { useMe, isAdmin } from "~/lib/auth";
import type { IOpsUser } from "~/lib/proto";

export const Route = createFileRoute("/_authed/ops/users")({
  component: Users,
});

function roleLabel(role?: string | null) {
  if (role === "ops_admin") return "Administrator";
  if (role === "ops_agent") return "Agent";
  return "Customer";
}

function isStaffRole(role?: string | null) {
  return role === "ops_admin" || role === "ops_agent";
}

function roleChangeBody(role: string) {
  if (role === "ops_admin") return "Administrators can manage rates, routes, users and all configuration.";
  if (role === "ops_agent") return "Agents can process parcels, tickets and payments, but not configuration.";
  return "They lose all operations access and keep only their customer account.";
}

function roleBadgeVariant(role?: string | null): "default" | "secondary" | "outline" {
  if (role === "ops_admin") return "default";
  if (role === "ops_agent") return "secondary";
  return "outline";
}

function Users() {
  const { data: me } = useMe();
  const admin = isAdmin(me);
  const [selected, setSelected] = useState<string | "new" | null>(null);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  const { data: rows, isLoading } = useQuery({
    queryKey: ["ops", "users"],
    queryFn: () => api.ops.listUsers(),
  });

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    let list = rows ?? [];
    if (roleFilter === "staff") list = list.filter((u) => isStaffRole(u.role));
    else if (roleFilter === "customer") list = list.filter((u) => !isStaffRole(u.role));
    if (!q) return list;
    return list.filter((u) => [u.name, u.phone, u.ppCode, roleLabel(u.role)].some((f) => f?.toLowerCase().includes(q)));
  }, [rows, q, roleFilter]);

  const columns: ColumnDef<IOpsUser, unknown>[] = [
    { header: "Name", accessorKey: "name", cell: ({ row }) => row.original.name ? <span className="font-medium">{row.original.name}</span> : <span className="text-text-faint italic">No name set</span> },
    { header: "Phone", accessorKey: "phone", cell: ({ row }) => <span className="font-mono text-[12px]">{row.original.phone}</span>, meta: { headClassName: "w-[160px]" } },
    { header: "Customer code", accessorKey: "ppCode", cell: ({ row }) => isStaffRole(row.original.role) ? <span className="text-text-faint">—</span> : <span className="font-mono text-[12px]">{row.original.ppCode || "—"}</span>, meta: { headClassName: "w-[150px]" } },
    { header: "Role", accessorKey: "role", cell: ({ row }) => <Badge variant={roleBadgeVariant(row.original.role)}>{roleLabel(row.original.role)}</Badge>, meta: { headClassName: "w-[140px]" } },
    { header: "Status", accessorKey: "active", cell: ({ row }) => row.original.active === false ? <Badge variant="secondary">Inactive</Badge> : <Badge variant="success">Active</Badge>, meta: { headClassName: "w-[100px]" } },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Configuration"
        title="Users & roles"
        caption="Operations staff and customer accounts"
        actions={admin ? <Button onClick={() => setSelected("new")}><Plus /> Invite user</Button> : undefined}
      />
      <div className="flex flex-wrap items-center gap-2">
        <SearchInput value={query} onChange={setQuery} className="max-w-sm" placeholder="Name, phone, customer code or role" />
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All users</SelectItem>
            <SelectItem value="staff">Staff</SelectItem>
            <SelectItem value="customer">Customers</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <MasterDetail
        open={selected !== null}
        onClose={() => setSelected(null)}
        detail={selected ? <UserPanel userId={selected === "new" ? null : selected} onClose={() => setSelected(null)} /> : null}
        emptyHint="Select a user to view them"
        title="User"
      >
        <DataTable
          data={filtered}
          columns={columns}
          limit={200}
          offset={0}
          onPageChange={() => {}}
          loading={isLoading}
          getRowId={(u) => u.id!}
          onRowClick={admin ? (u) => setSelected(u.id!) : undefined}
          selectedRowId={selected && selected !== "new" ? selected : undefined}
          emptyTitle={q ? "No matches" : "No users yet"}
          emptyBody={q ? "Try a different search term." : "Invite an operations agent or administrator."}
        />
      </MasterDetail>
    </>
  );
}

function UserPanel({ userId, onClose }: { userId: string | null; onClose: () => void }) {
  if (userId === null) return <InviteUser onClose={onClose} />;
  return <ManageUser userId={userId} />;
}

function ManageUser({ userId }: { userId: string }) {
  const { data: me } = useMe();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);
  const { data: users } = useQuery({ queryKey: ["ops", "users"], queryFn: () => api.ops.listUsers() });
  const user = users?.find((u) => u.id === userId);

  if (!user) return <div className="text-text-faint p-10 text-center text-[13px]">User not found.</div>;

  const admin = isAdmin(me);
  const isSelf = me?.id === user.id;
  const label = user.name || user.phone || "User";
  const currentRole = isStaffRole(user.role) ? user.role! : "customer";

  async function apply(patch: { role?: string; active?: boolean }, message: string) {
    setBusy(true);
    try {
      await api.ops.updateUser(user!.id!, patch);
      toast.success(message);
      void qc.invalidateQueries({ queryKey: ["ops", "users"] });
    } catch (e) {
      toast.error(e instanceof APIError ? e.message : "Could not update user");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="space-y-4 p-4 pr-10">
        <div>
          <p className="eyebrow">User</p>
          <h2 className="section-heading mt-0.5">{user.name || "No name set"}</h2>
          <div className="mt-2 flex items-center gap-1.5">
            <Badge variant={roleBadgeVariant(user.role)}>{roleLabel(user.role)}</Badge>
            {user.active === false ? <Badge variant="secondary">Inactive</Badge> : <Badge variant="success">Active</Badge>}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <DetailField label="Phone"><span className="font-mono text-[12px]">{user.phone}</span></DetailField>
          <DetailField label="Role">{roleLabel(user.role)}</DetailField>
        </div>

        {admin && !isSelf && (
          <div className="space-y-2">
            <p className="eyebrow">Manage</p>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select
                value={currentRole}
                disabled={busy}
                onValueChange={async (next) => {
                  if (next === currentRole) return;
                  const ok = await confirm({
                    title: `Make ${label} ${roleLabel(next)}?`,
                    body: roleChangeBody(next),
                    confirmLabel: `Make ${roleLabel(next)}`,
                    destructive: next === "customer",
                  });
                  if (ok) void apply({ role: next }, `${label} → ${roleLabel(next)}`);
                }}
              >
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="ops_agent">Agent</SelectItem>
                  <SelectItem value="ops_admin">Administrator</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-text-faint text-[12px]">Takes effect for them within a few minutes, or at their next sign-in.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {user.active === false ? (
                <Button size="sm" variant="outline" loading={busy} onClick={() => void apply({ active: true }, `${label} reactivated`)}>
                  Reactivate
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-destructive"
                  loading={busy}
                  onClick={async () => {
                    const ok = await confirm({
                      title: "Deactivate account?",
                      body: `${label} will lose access immediately.`,
                      confirmLabel: "Deactivate",
                      destructive: true,
                    });
                    if (ok) void apply({ active: false }, `${label} deactivated`);
                  }}
                >
                  Deactivate
                </Button>
              )}
            </div>
          </div>
        )}
        {isSelf && <p className="text-text-faint text-[12px]">You can't change your own role or status.</p>}
      </div>
    </div>
  );
}

function InviteUser({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("ops_agent");

  async function save() {
    if (!phone.trim()) {
      toast.error("Phone is required");
      return;
    }
    setBusy(true);
    try {
      await api.ops.createUser({ phone: phone.trim(), role, name: name.trim() || undefined });
      toast.success("User invited");
      void qc.invalidateQueries({ queryKey: ["ops", "users"] });
      onClose();
    } catch (e) {
      toast.error(e instanceof APIError ? e.message : "Could not create user");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="space-y-4 p-4 pr-10">
        <div>
          <p className="eyebrow">User</p>
          <h2 className="section-heading mt-0.5">Invite user</h2>
          <p className="text-text-faint mt-1 text-[13px]">They sign in by phone and set their own password.</p>
        </div>
        <div className="space-y-1.5"><Label>Phone number</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="07xxx or 01xxx" /></div>
        <div className="space-y-1.5"><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Optional" /></div>
        <div className="space-y-1.5">
          <Label>Role</Label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ops_agent">Agent</SelectItem>
              <SelectItem value="ops_admin">Administrator</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button className="w-full" loading={busy} onClick={() => void save()}>Invite user</Button>
      </div>
    </div>
  );
}
